import React, { useCallback, useEffect, useState } from "react";
import { Button, Col, Form, Row, Spinner } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import api from "../services/api";
import { extractArray } from "../utils/apiData";
import { formatDateForApi } from "../utils/formatters";

const normalizeShops = (items) => {
  const unique = new Map();

  items.forEach((item) => {
    const id = item.id ?? item.shopid ?? item.shop_id;
    if (id === undefined || id === null) return;

    unique.set(String(id), {
      id,
      name: item.name || item.shop_name || `Shop ${id}`,
    });
  });

  return Array.from(unique.values());
};

const Filters = ({ filters, onFilterChange }) => {
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchShops = useCallback(async () => {
    setLoading(true);
    try {
      let response;
      try {
        response = await api.getShops();
      } catch {
        response = await api.getShopRevenue({
          start_date: formatDateForApi(filters.startDate),
          end_date: formatDateForApi(filters.endDate),
        });
      }

      setShops(normalizeShops(extractArray(response)));
    } catch (error) {
      console.error("Unable to load shops:", error);
      setShops([]);
    } finally {
      setLoading(false);
    }
  }, [filters.endDate, filters.startDate]);

  useEffect(() => {
    fetchShops();
  }, [fetchShops]);

  const handleReset = () => {
    onFilterChange({
      startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
      endDate: new Date(),
      shopId: "",
    });
  };

  return (
    <section className="filter-panel mb-4" aria-label="Dashboard filters">
      <Row className="align-items-end g-3">
        <Col sm={6} lg={3}>
          <Form.Group>
            <Form.Label>Start Date</Form.Label>
            <DatePicker
              selected={filters.startDate}
              onChange={(date) => onFilterChange({ startDate: date })}
              className="form-control"
              dateFormat="yyyy-MM-dd"
              maxDate={filters.endDate}
              required
            />
          </Form.Group>
        </Col>
        <Col sm={6} lg={3}>
          <Form.Group>
            <Form.Label>End Date</Form.Label>
            <DatePicker
              selected={filters.endDate}
              onChange={(date) => onFilterChange({ endDate: date })}
              className="form-control"
              dateFormat="yyyy-MM-dd"
              minDate={filters.startDate}
              maxDate={new Date()}
              required
            />
          </Form.Group>
        </Col>
        <Col sm={6} lg={3}>
          <Form.Group>
            <Form.Label>Shop</Form.Label>
            <Form.Select
              value={filters.shopId}
              onChange={(event) => onFilterChange({ shopId: event.target.value })}
              disabled={loading}
            >
              <option value="">All Shops</option>
              {shops.map((shop) => (
                <option key={shop.id} value={shop.id}>
                  {shop.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col sm={6} lg={3}>
          <div className="d-flex gap-2">
            <Button variant="outline-secondary" onClick={handleReset}>
              Reset
            </Button>
            <Button variant="dark" onClick={fetchShops} disabled={loading}>
              {loading ? (
                <>
                  <Spinner size="sm" animation="border" className="me-2" />
                  Loading
                </>
              ) : (
                "Refresh Shops"
              )}
            </Button>
          </div>
        </Col>
      </Row>
    </section>
  );
};

export default Filters;
